/**
 * Hook for checking user permissions based on their role
 * @format
 */

import { useAuthStore } from '../../features/auth/store';
import {
  Permission,
  roleHasPermission,
  roleHasAnyPermission,
  roleHasAllPermissions,
  roleCanAccessDrafts,
  getDefaultRouteForRole,
} from '../../features/auth/permissions';
import type { UserRole } from '../../features/auth/type';

export interface UsePermissionsReturn {
  /** Check if user has a specific permission */
  hasPermission: (permission: Permission) => boolean;
  /** Check if user has any of the specified permissions */
  hasAnyPermission: (permissions: Permission[]) => boolean;
  /** Check if user has all of the specified permissions */
  hasAllPermissions: (permissions: Permission[]) => boolean;
  /** Check if user can access draft products */
  canAccessDrafts: () => boolean;
  /** Get the default route for the current user's role */
  getDefaultRoute: () => string;
  /** User's current role */
  role: UserRole | undefined;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
}

/**
 * Hook to check permissions for the current user
 *
 * @example
 * const { hasPermission, canAccessDrafts } = usePermissions();
 *
 * if (hasPermission(Permission.PRODUCT_CREATE)) {
 *   // Show create button
 * }
 */
export const usePermissions = (): UsePermissionsReturn => {
  const { user, isAuthenticated } = useAuthStore();
  const role = user?.roleName;

  // PE-2095 (verified): frontend gates authorize off the static RolePermissions map keyed by the
  // user's SINGLE `roleName`. For a user who co-holds roles (e.g. COLLECTIVE_SUPERADMIN + ADMIN),
  // the JWT `p` claim carries the UNION but `roleName` is one role, so the extra role's nav is
  // under-granted here. Not fixed via `user.permissions` because that claim is watchman's uppercase
  // Resource:Action vocabulary (e.g. `MERCHANT:['READ']`) — a different vocabulary from these
  // Permission strings (`merchant:view`, `invoice:void`, `product:read:all`), and many gates
  // (METADATA/CHARTER/PORTFOLIO/SLIDER/DASHBOARD) have NO watchman Resource, so a claim-based
  // rewrite needs a hand-built translation table. Deferred to its own ticket: the gap is
  // nav-visibility only — osprey-api enforces the real per-permission server-side off the same claim.
  return {
    hasPermission: (permission: Permission) => roleHasPermission(role, permission),
    hasAnyPermission: (permissions: Permission[]) => roleHasAnyPermission(role, permissions),
    hasAllPermissions: (permissions: Permission[]) => roleHasAllPermissions(role, permissions),
    canAccessDrafts: () => roleCanAccessDrafts(role),
    getDefaultRoute: () => getDefaultRouteForRole(role),
    role,
    isAuthenticated,
  };
};

// Re-export for convenience
export { Permission, getDefaultRouteForRole } from '../../features/auth/permissions';
